import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { debounceTime, distinctUntilChanged } from 'rxjs';

import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import {
  byBusinessThenAlpha,
  isBusinessRole,
} from '../../../shared/helpers/role.helpers';
import { RolesService } from '../../../shared/services/roles.service';
import { RoleDTO } from '../../../shared/types/role.types';

@Component({
  selector: 'app-roles-list',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    PageHeaderComponent,
    DataStateComponent,
  ],
  templateUrl: './roles-list.component.html',
})
export class RolesListComponent implements OnInit {
  private readonly rolesService = inject(RolesService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  /** Sorted once after fetch (business roles first). */
  readonly roles = signal<RoleDTO[]>([]);

  readonly searchCtrl = this.fb.control('');
  private readonly searchSignal = signal('');

  /** Filtered view — sort happens at fetch time, only the predicate runs here. */
  readonly filteredRoles = computed(() => {
    const q = this.searchSignal().toLowerCase().trim();
    if (!q) return this.roles();
    return this.roles().filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        (r.description?.toLowerCase().includes(q) ?? false),
    );
  });

  // expose helper for template
  readonly isBusinessRole = isBusinessRole;

  ngOnInit(): void {
    this.searchCtrl.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) => this.searchSignal.set(value));

    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);

    this.rolesService
      .list()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.roles.set([...(res ?? [])].sort(byBusinessThenAlpha));
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.roles.set([]);
          this.error.set(toErrorMessage(err));
        },
      });
  }
}
